/**
 * useGalleryFilters
 *
 * Derives the visible screenshot list from the loaded library and the current
 * navbar filter state — a free-text search (name/description/area/tags) plus
 * tag / planet / author / sharing-status filters that AND together. The result
 * is then ordered by the current sort (field + direction). Pure client-side
 * filtering + sorting over the already-loaded list.
 */

import { useMemo } from 'react';
import {
  asTrlDifficulty,
  TRL_DIFFICULTY_RANK,
  type TrlImageAsset,
} from '@swtorbit/shared-socket-events';

/** Sharing-status filter buckets. */
export type SharingFilter = 'all' | 'private' | 'shared-by-me' | 'shared-by-others';

/** Current navbar filter state. */
export interface GalleryFilters {
  /** Free-text query matched against name/description/area/tags. */
  search: string;
  /** Selected tags — an image matches if it has ANY of them (empty = no filter). */
  tags: string[];
  /** Selected planet/area, or null for all. */
  area: string | null;
  /** Selected author username (createdByName), or null for all. */
  author: string | null;
  /** Sharing-status bucket. */
  sharing: SharingFilter;
}

export const EMPTY_FILTERS: GalleryFilters = {
  search: '',
  tags: [],
  area: null,
  author: null,
  sharing: 'all',
};

/** Which attribute the gallery is ordered by. */
export type SortField = 'createdAt' | 'area' | 'name' | 'author' | 'difficulty' | 'faction';

/** Sort direction — ascending or descending. */
export type SortDirection = 'asc' | 'desc';

/** Current gallery sort state (field + direction). */
export interface GallerySort {
  field: SortField;
  direction: SortDirection;
}

/** Default order — newest-first, matching the gallery's historical behaviour. */
export const DEFAULT_SORT: GallerySort = { field: 'createdAt', direction: 'desc' };

function matchesSharing(image: TrlImageAsset, sharing: SharingFilter): boolean {
  switch (sharing) {
    case 'private':
      return image.isOwn !== false && !image.isShared;
    case 'shared-by-me':
      return image.isOwn !== false && !!image.isShared;
    case 'shared-by-others':
      return image.isOwn === false;
    case 'all':
    default:
      return true;
  }
}

/**
 * Compare two images by the chosen sort field, respecting direction. String
 * fields use locale-aware compare; difficulty uses the rank map above. Images
 * missing the sort value (no author/difficulty/faction) always sort last,
 * regardless of direction. `createdAt` (newest-first) breaks ties so ordering
 * is stable.
 */
function compareBySort(a: TrlImageAsset, b: TrlImageAsset, sort: GallerySort): number {
  const dir = sort.direction === 'asc' ? 1 : -1;

  let primary = 0;
  switch (sort.field) {
    case 'createdAt':
      primary = (a.createdAt - b.createdAt) * dir;
      break;
    case 'area':
      primary = a.area.localeCompare(b.area) * dir;
      break;
    case 'name':
      primary = a.name.localeCompare(b.name) * dir;
      break;
    case 'author':
      primary = compareOptionalStrings(a.createdByName, b.createdByName, dir);
      break;
    case 'faction':
      primary = compareOptionalStrings(a.faction, b.faction, dir);
      break;
    case 'difficulty':
      primary = compareDifficulty(a.difficulty, b.difficulty, dir);
      break;
  }

  if (primary !== 0) return primary;
  // Stable tie-break: newest-first.
  return b.createdAt - a.createdAt;
}

/** localeCompare two optional strings; missing values sort last in both directions. */
function compareOptionalStrings(a: string | undefined, b: string | undefined, dir: number): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b) * dir;
}

/** Compare by difficulty rank; missing/unknown difficulties sort last in both directions. */
function compareDifficulty(a: string | undefined, b: string | undefined, dir: number): number {
  const da = asTrlDifficulty(a);
  const db = asTrlDifficulty(b);
  const ra = da ? TRL_DIFFICULTY_RANK[da] : undefined;
  const rb = db ? TRL_DIFFICULTY_RANK[db] : undefined;
  if (ra === undefined && rb === undefined) return 0;
  if (ra === undefined) return 1;
  if (rb === undefined) return -1;
  return (ra - rb) * dir;
}

export function useGalleryFilters(
  images: TrlImageAsset[],
  filters: GalleryFilters,
  sort: GallerySort,
): TrlImageAsset[] {
  return useMemo(() => {
    const query = filters.search.trim().toLowerCase();

    const filtered = images.filter((image) => {
      if (filters.area && image.area !== filters.area) return false;
      if (filters.author && image.createdByName !== filters.author) return false;
      if (!matchesSharing(image, filters.sharing)) return false;
      if (filters.tags.length > 0 && !filters.tags.some((t) => image.tags.includes(t))) return false;
      if (query) {
        const haystack = [
          image.name,
          image.description,
          image.area,
          ...image.tags,
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });

    return filtered.sort((a, b) => compareBySort(a, b, sort));
  }, [images, filters, sort]);
}
